import { useEffect, useState } from "react";
import { Lightbulb, Loader2 } from "lucide-react";

/* Engaging, on-brand "while you wait" panel shown during LLM scans/optimizations.
   Rotates through pro tips + job-hunt facts. When a numeric `progress` (0-100) is
   passed it renders a DETERMINATE filling bar; otherwise an indeterminate shimmer.
   Theme-aware via shadcn tokens (bg-card / text-foreground / bg-primary …). */

const TIPS = [
  { tag: "Tip while you wait", t: "Tailor beats volume", d: "A resume matched to the job description can earn up to 3× more callbacks than a generic one." },
  { tag: "Did you know?", t: "Recruiters skim in ~7 seconds", d: "Front-load your most relevant wins so they land in that first glance." },
  { tag: "Tip while you wait", t: "Numbers get noticed", d: "Bullets with a metric (%, $, time saved) are far more memorable than plain duties." },
  { tag: "Tip while you wait", t: "Mirror the language", d: "Using the exact skill terms from the JD helps you clear ATS keyword filters." },
  { tag: "Did you know?", t: "The 250-applicant race", d: "The average corporate job posting attracts around 250 applicants — standing out is everything." },
  { tag: "Tip while you wait", t: "Verbs, not adjectives", d: "Start bullets with action verbs — 'Led', 'Built', 'Cut' — not 'Responsible for'." },
  { tag: "Tip while you wait", t: "Keep the skills scannable", d: "A clean skills section makes it easy for both ATS and humans to confirm a match." },
  { tag: "How we work", t: "Honest by design", d: "We only add the skills you approve, woven into your real experience — never fabricated." },
  { tag: "Did you know?", t: "Consistency wins", d: "Tailoring every application, even lightly, compounds into far more interviews over a month." },
];

export function WaitingUX({ label, variant = "full", className = "", progress = null }) {
  const compact = variant === "compact";
  const [i, setI] = useState(() => Math.floor(Math.random() * TIPS.length));
  const determinate = typeof progress === "number";

  useEffect(() => {
    const id = setInterval(() => setI((p) => (p + 1) % TIPS.length), 4200);
    return () => clearInterval(id);
  }, []);

  const tip = TIPS[i];

  return (
    <div className={className} data-testid="waiting-ux">
      {(label || determinate) && (
        <div className={`flex items-center justify-between gap-2 ${compact ? "text-xs" : "text-sm"} text-muted-foreground mb-2`} data-testid="waiting-ux-label">
          <span className="flex items-center gap-2 min-w-0 truncate">
            <Loader2 className="animate-spin text-primary shrink-0" size={compact ? 13 : 15} /> {label}
          </span>
          {determinate && (
            <span className="font-mono font-bold text-foreground shrink-0 tabular-nums" data-testid="waiting-ux-percent">{Math.round(progress)}%</span>
          )}
        </div>
      )}

      {determinate ? (
        <div className={`relative w-full overflow-hidden rounded-full bg-muted ${compact ? "h-2" : "h-2.5"} mb-4`} data-testid="waiting-ux-bar">
          <span
            className="absolute inset-y-0 left-0 rounded-full bg-primary"
            style={{ width: `${Math.max(3, Math.min(100, progress))}%`, transition: "width 0.35s cubic-bezier(0.22,1,0.36,1)" }}
          />
          <span className="wux-glow absolute inset-y-0 rounded-full" style={{ left: `calc(${Math.max(3, Math.min(100, progress))}% - 28px)`, width: 28 }} />
        </div>
      ) : (
        <div className={`relative w-full overflow-hidden rounded-full bg-muted ${compact ? "h-1" : "h-1.5"} mb-4`}>
          <span className="wux-shimmer absolute inset-y-0 left-0 rounded-full bg-primary" />
        </div>
      )}

      <div key={i} className={`wux-fade flex items-start gap-3 rounded-xl border border-border bg-card ${compact ? "p-3" : "p-4"}`} data-testid="waiting-ux-tip">
        <span className={`shrink-0 rounded-lg bg-primary/10 flex items-center justify-center ${compact ? "w-7 h-7" : "w-9 h-9"}`}>
          <Lightbulb className="text-primary" size={compact ? 14 : 17} />
        </span>
        <div className="min-w-0">
          <div className="text-[0.6rem] font-mono uppercase tracking-[0.15em] text-muted-foreground mb-0.5">{tip.tag}</div>
          <div className={`font-semibold text-foreground ${compact ? "text-xs" : "text-sm"} leading-snug`}>{tip.t}</div>
          <div className={`text-muted-foreground ${compact ? "text-[0.72rem]" : "text-[0.82rem]"} leading-snug mt-0.5`}>{tip.d}</div>
        </div>
      </div>

      <div className="flex items-center justify-center gap-1.5 mt-3" data-testid="waiting-ux-dots">
        {TIPS.map((_, k) => (
          <span key={k} className={`rounded-full transition-all duration-300 h-1.5 ${k === i ? "w-4 bg-primary" : "w-1.5 bg-muted-foreground/30"}`} />
        ))}
      </div>

      <style>{`
        @keyframes wux-shimmer { 0%{transform:translateX(-100%);width:40%} 50%{width:70%} 100%{transform:translateX(240%);width:40%} }
        .wux-shimmer { animation: wux-shimmer 1.5s ease-in-out infinite; }
        @keyframes wux-fade { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:translateY(0)} }
        .wux-fade { animation: wux-fade .45s ease-out; }
        .wux-glow { background: radial-gradient(ellipse at center, color-mix(in srgb, var(--primary) 70%, white) 0%, transparent 70%); filter: blur(2px); opacity:.8; }
      `}</style>
    </div>
  );
}

export default WaitingUX;
